// Marketing copy mirrored from the joylo-web pricing page (src/components/PricingCards.tsx).
// The billing API returns pricing/limits but no positioning copy, so the add-on and
// "Included" boxes are keyed here by plan name and merged onto the API plan at render time.

export type AddOnItem = {
  title: string;
  kind: string;
  bullet: string;
};

// One line inside the green "Included" box on Co-Build plans: a bold claim
// with the supporting detail underneath (hours and response time, or the
// standalone price of something bundled in).
export type IncludedItem = {
  title: string;
  detail: string;
};

export type PlanCopy = {
  target?: string;
  features?: string[];
  // Short heading above the feature list, e.g. "All Solo Builder features included:".
  featuresLead?: string;
  addOns?: AddOnItem[];
  includedItems?: IncludedItem[];
  includedFootnote?: string;
  // Optional "See what's included" link shown after the footnote (Co-Build 10 points at its joylo-web offer page)
  includedFootnoteLink?: string;
  badgeLabel?: string;
  // One line under the credits value explaining what that many credits actually covers in
  // practice, mirrored from joylo-web's PricingPlan.creditsNote.
  creditsNote?: string;
  // Full replacement for the computed "N credits/month" line — only Free needs this today,
  // since its real allowance ("30 credits 1st month, then 10/month") isn't a single figure
  // formatPlanCredits can derive from the API's plain monthly/daily fields.
  creditsOverride?: string;
  // "Unlock with X" teaser for the Free card, which otherwise sits half-empty beside the taller paid cards.
  upsell?: {
    planLabel: string;
    items: string[];
  };
  // Intro-offer framing (Trial, Co-Build 10): green offer chip, optional struck-through regular price, renewal line.
  promo?: {
    badge: string;
    badgeDetail?: string;
    wasPrice?: number;
    priceSuffix: string;
    // Omitted when the renewal is spelled out elsewhere on the card, so the price block keeps the same height as its neighbours
    renewNote?: string;
  };
  // Button label override, and whether the button takes the gradient (popular) style without the "Most Popular" badge.
  ctaLabel?: string;
  primaryCta?: boolean;
};

const CO_BUILD_FOOTNOTE =
  "Bringing an app across? Use your hours for the migration. Need more in a heavy month, top up at $500.";

const PRODUCTION_SUPPORT_INCLUDED: IncludedItem = {
  title: "Production Support, Advanced",
  detail: "$999 a month on its own",
};

const SELF_SERVE_ADD_ONS: AddOnItem[] = [
  {
    title: "Expert Assist: 10 architect hours for $500",
    kind: "Certified Joylo Architect",
    bullet: "Certified in-house engineers on demand, inside your project, business hours.",
  },
  {
    title: "Production Support from $299/month",
    kind: "Managed monitoring",
    bullet: "24/7 automated monitoring, with engineers on it during business hours.",
  },
];

// Co-Build 10: one month of engineering on top of Starter, then plain Starter. Mirrors joylo-web src/lib/co-build-10.ts.
export const CO_BUILD_10_OFFER = {
  name: "Co-Build 10",
  badge: "Introductory offer",
  headline: "Your idea. A working MVP. For $500.",
  pitch: "Build with Joylo AI and a real engineer. Get 10 hours of hands-on help to scope, build and review your first MVP.",
  includedCopy: "Starter included with 250 AI credits. Then $50/month for Starter. Cancel anytime.",
  scopeCopy: "First-month offer. MVP scope agreed to fit 10 engineering hours; extra work agreed and priced first.",
  detailsUrl: "https://joylo.ai/co-build-10",
  detailsLabel: "See what’s included",
  cta: "Buy for $500",
  price: 500,
  hours: 10,
  credits: 250,
  renewalPrice: 50,
  renewalPlan: "Starter",
};

const namedArchitect = (hours: number, responseHours: number, fullTime = false): IncludedItem => ({
  title: fullTime ? "A full-time Joylo Architect" : "A named Joylo Architect",
  detail: `${hours} hours a month, ${responseHours} hour response, with engineers on it during business hours (9am-5pm GMT, Mon-Fri)`,
});

// Keys are normalised plan names (lowercase, non-alphanumerics stripped) so a plan
// stored as "co_build_40", "co-build-40" or "coBuild40" all resolve to the same copy.
const PLAN_COPY: Record<string, PlanCopy> = {
  free: {
    target: "Beginners, hobbyists, and testers",
    creditsOverride: "30 credits 1st month, then 10/month",
    creditsNote: "Enough to explore Joylo and get a first idea on screen.",
    features: [
      "3 public apps",
      "1GB storage",
      "AI Confidence Score",
      "Community support",
    ],
    upsell: {
      planLabel: "Solo Builder",
      items: [
        "100 AI credits every month",
        "10 private apps",
        "5GB storage",
        "Custom domains",
        "GitHub integration and version control",
        "Shared team workspaces with unlimited members",
        "Figma import",
        "AI import from other app builders",
        "Purchase extra credits as needed",
        "Top-up credit rollovers",
        "Remove the Joylo badge",
        "Free standard migration",
        "Full ownership of your app",
      ],
    },
  },
  trialplan: {
    target: "Solo Builder for one person. Just $1.",
    creditsNote: "Enough to try Joylo and get a feel for building your first project.",
    featuresLead: "All Solo Builder features except the team workspace:",
    features: [
      "10 private apps",
      "5GB storage",
      "Custom domains",
      "GitHub integration and version control",
      "Figma import",
      "AI import from other app builders",
      "Purchase extra credits as needed",
      "Remove the Joylo badge",
      "Top-up credit rollovers",
      "Free standard migration",
      "AI Confidence Score",
      "Full ownership of your app",
    ],
    promo: {
      badge: "96% off",
      badgeDetail: "First month",
      wasPrice: 25,
      priceSuffix: "for your first month",
      renewNote: "Automatically renews to Solo Builder at $25 / month with 100 AI credits. Cancel anytime.",
    },
    addOns: SELF_SERVE_ADD_ONS,
  },
  solobuilder: {
    target: "Solo founders, non-technical users",
    creditsNote: "Enough to build your first MVP, with three or four features properly finished.",
    features: [
      "Everything in Free",
      "10 private apps",
      "5GB storage",
      "Custom domains",
      "GitHub integration and version control",
      "Figma import",
      "AI import from other app builders",
      "Purchase extra credits as needed",
      "Remove the Joylo badge",
      "Top-up credit rollovers",
      "Free standard migration",
      "AI Confidence Score",
      "Full ownership of your app",
      "Shared team workspaces with unlimited members",
      "Role-based access",
    ],
    addOns: SELF_SERVE_ADD_ONS,
  },
  starter: {
    target: "Individual developers and small projects",
    creditsNote: "Enough to complete a mid-size app and launch it in production.",
    features: [
      "Everything in Solo Builder",
      "Unlimited private apps",
      "10GB storage",
      "Basic analytics",
      "Company SSO for your Joylo workspace",
    ],
    addOns: SELF_SERVE_ADD_ONS,
  },
  cobuild10: {
    target: CO_BUILD_10_OFFER.headline,
    features: [
      "All Starter features",
      "We scope, build and review your first working version",
    ],
    includedItems: [
      {
        title: "An available Joylo engineer",
        detail: `Joylo AI + ${CO_BUILD_10_OFFER.hours} hours of hands-on engineering to scope, build and review your first MVP.`,
      },
      {
        title: "Starter included",
        detail: `${CO_BUILD_10_OFFER.credits} AI credits / month. Then $${CO_BUILD_10_OFFER.renewalPrice}/month for ${CO_BUILD_10_OFFER.renewalPlan}, cancel anytime.`,
      },
    ],
    includedFootnote: "MVP scope agreed to fit your included hours. Engineering in month one only; extra work agreed and priced first.",
    includedFootnoteLink: CO_BUILD_10_OFFER.detailsUrl,
    promo: {
      badge: CO_BUILD_10_OFFER.badge,
      priceSuffix: "/first month",
    },
    ctaLabel: CO_BUILD_10_OFFER.cta,
    primaryCta: true,
  },
  cobuild40: {
    target: "Developers, serious startups, growing businesses",
    features: [
      "Everything in Starter",
      "Code reviews and optimisation",
      "Architecture guidance",
      "Production deployment help",
    ],
    includedItems: [namedArchitect(40, 4), PRODUCTION_SUPPORT_INCLUDED],
    includedFootnote: CO_BUILD_FOOTNOTE,
  },
  cobuild80: {
    target: "Growing teams, scaling startups",
    features: [
      "Everything in Co-Build 40",
      "More dedicated time for scaling",
      "Performance optimisation",
      "Advanced production support",
    ],
    badgeLabel: "Best value per hour",
    includedItems: [namedArchitect(80, 4), PRODUCTION_SUPPORT_INCLUDED],
    includedFootnote: CO_BUILD_FOOTNOTE,
  },
  cobuild160: {
    target: "Large teams, enterprises",
    features: [
      "Everything in Co-Build 80",
      "Full-stack development",
      "Executive reporting",
      "Custom integrations",
    ],
    includedItems: [namedArchitect(160, 2, true), PRODUCTION_SUPPORT_INCLUDED],
    includedFootnote: CO_BUILD_FOOTNOTE,
  },
  enterpriseprivatecloud: {
    target: "Large enterprises with security/compliance needs",
    features: [
      "Dedicated architect team, hours agreed with you, with engineers on it during business hours (9am-5pm GMT, Mon-Fri)",
      "Production Support across every app",
      "Unlimited migrations",
      "Bring your own cloud: private deployment on your infrastructure",
      "Custom security policies and SLA guarantees",
      "White-label options",
    ],
  },
  enterprisepubliccloud: {
    target: "Enterprises preferring public cloud instances",
    features: [
      "Bring your own cloud: deployment in your public cloud account",
      "Enhanced security & compliance",
      "Custom configurations",
      "Advanced monitoring, with engineers on it during business hours (9am-5pm GMT, Mon-Fri)",
      "Priority feature requests",
    ],
  },
};

const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

// "Expert Builder" is the checkout name joylo-web links use for Co-Build 10 (?plan=Expert%20Builder).
const CO_BUILD_10_ALIASES = ["cobuild10", "expertbuilder"];

export const isCoBuild10Plan = (name?: string, displayName?: string): boolean =>
  [name, displayName].some((candidate) => !!candidate && CO_BUILD_10_ALIASES.includes(normalise(candidate)));

// Co-Build 10/40/80/160 lead with an engineer call; Enterprise shares the tab but is not one of them.
export const isCoBuildPlan = (name?: string, displayName?: string): boolean =>
  isCoBuild10Plan(name, displayName) || [name, displayName].some((candidate) => !!candidate && normalise(candidate).startsWith("cobuild"));

export type PlanTrack = "self" | "cobuild";

// Engineer-led plans (Co-Build, Enterprise) go under the "Co-Build with an engineer" tab; everything else is self-serve.
export const getPlanTrack = (name: string, displayName?: string): PlanTrack => {
  if (isCoBuild10Plan(name, displayName)) return "cobuild";
  const key = normalise(`${name} ${displayName ?? ""}`);
  return key.includes("cobuild") || key.includes("enterprise") ? "cobuild" : "self";
};

// Matches on the normalised plan name, then on display name, so copy still resolves
// when the DB name and the marketing label drift apart.
export const getPlanCopy = (name: string, displayName?: string): PlanCopy => {
  if (isCoBuild10Plan(name, displayName)) return PLAN_COPY.cobuild10;
  for (const candidate of [name, displayName]) {
    if (!candidate) continue;
    const key = normalise(candidate);
    if (PLAN_COPY[key]) return PLAN_COPY[key];
    // "free_tier" / "plan_free_v2" should still land on the free copy. Longest key wins so
    // "cobuild160" is never shadowed by a shorter key that happens to be a substring.
    const contained = Object.keys(PLAN_COPY)
      .filter((planKey) => key.includes(planKey))
      .sort((a, b) => b.length - a.length)[0];
    if (contained) return PLAN_COPY[contained];
  }
  return {};
};
